import type { ApiError, ApiResponse, PublicUser } from '@apartment-booking/shared';
export { FRIENDS_USER_ID, FRIENDS_USER_NAME } from '@apartment-booking/shared';

export const API_BASE_URL = import.meta.env.PROD
  ? 'https://api.5h.hellers.com/api'
  : '/api';
const API_BASE = API_BASE_URL;

let csrfTokenCache: string | null = null;

function normalizeToken(value: string | null | undefined): string | null {
  if (!value) return null;
  const token = value.replace(/\u0000/g, '').trim();
  return token || null;
}

export function setCsrfToken(token: string | null): void {
  csrfTokenCache = normalizeToken(token);
}

// Get CSRF token from cookie
function getCsrfToken(): string | null {
  if (csrfTokenCache) {
    return csrfTokenCache;
  }
  const match = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]*)/);
  return normalizeToken(match ? match[1] : null);
}

interface RequestOptions extends RequestInit {
  json?: unknown;
}

class ApiClient {
  private async refreshCsrfToken(): Promise<void> {
    const response = await fetch(`${API_BASE}/auth/me`, {
      method: 'GET',
      credentials: 'include',
    });

    if (!response.ok) {
      setCsrfToken(null);
      return;
    }

    const data = await response.json() as ApiResponse<{ user: PublicUser | null; csrfToken?: string }>;
    setCsrfToken(data.data.user ? (data.data.csrfToken || null) : null);
  }

  private async request<T>(
    endpoint: string,
    options: RequestOptions = {},
    allowCsrfRefreshRetry: boolean = true
  ): Promise<T> {
    const { json, ...init } = options;

    const headers: Record<string, string> = {};

    if (json) {
      headers['Content-Type'] = 'application/json';
    }

    // Include CSRF token for state-changing requests
    const method = (init.method || 'GET').toUpperCase();
    if (['POST', 'PUT', 'DELETE', 'PATCH'].includes(method)) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
      }
    }

    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...init,
      headers,
      body: json ? JSON.stringify(json) : init.body,
      credentials: 'include',
    });

    const data = await response.json();

    if (!response.ok) {
      const error = data as ApiError;

      if (
        allowCsrfRefreshRetry &&
        response.status === 403 &&
        error.error.message === 'Invalid CSRF token'
      ) {
        await this.refreshCsrfToken();
        return this.request<T>(endpoint, options, false);
      }

      throw new ApiRequestError(
        error.error.message,
        error.error.code,
        response.status,
        error.error.field,
        error.error.details
      );
    }

    return (data as ApiResponse<T>).data;
  }

  get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  post<T>(endpoint: string, json?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'POST', json });
  }

  put<T>(endpoint: string, json?: unknown): Promise<T> {
    return this.request<T>(endpoint, { method: 'PUT', json });
  }

  delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }
}

export class ApiRequestError extends Error {
  constructor(
    message: string,
    public code: string,
    public status: number,
    public field?: string,
    public details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }
}

export const api = new ApiClient();

// Auth API
export const authApi = {
  login: (email: string, password: string, stayLoggedIn: boolean = false) =>
    api.post<{ user: PublicUser; csrfToken: string }>('/auth/login', { email, password, stayLoggedIn }),

  logout: () => api.post<{ success: boolean }>('/auth/logout'),

  me: () => api.get<{ user: PublicUser | null; csrfToken?: string }>('/auth/me'),

  forgotPassword: (email: string) =>
    api.post<{ success: boolean }>('/auth/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post<{ success: boolean }>('/auth/reset-password', { token, password }),
};

// Users API
export const usersApi = {
  list: () => api.get<{ users: PublicUser[] }>('/users'),

  invite: (email: string, role: 'owner' | 'admin' | 'user') =>
    api.post<{ success: boolean; email: string }>('/users/invite', { email, role }),

  getInvitations: () =>
    api.get<{
      invitations: Array<{
        id: string;
        email: string;
        role: string;
        expires_at: string;
        created_at: string;
        invited_by: string;
        is_expired: number;
      }>;
    }>('/users/invitations'),

  revokeInvitation: (id: string) =>
    api.delete<{ success: boolean }>(`/users/invitations/${id}`),

  resendInvitation: (id: string) =>
    api.post<{ success: boolean; email: string }>(`/users/invitations/${id}/resend`),

  validateInvitation: (token: string) =>
    api.get<{
      valid: boolean;
      email: string;
      role: string;
      expiresAt: string;
    }>(`/users/signup/validate?token=${token}`),

  signup: (token: string, name: string, password: string) =>
    api.post<{ user: PublicUser }>('/users/signup', { token, name, password }),

  update: (id: string, data: { name?: string; role?: string; is_active?: boolean }) =>
    api.put<{ success: boolean }>(`/users/${id}`, data),

  updateColor: (id: string, color: string, pattern: string = 'solid') =>
    api.put<{ success: boolean }>(`/users/${id}/color`, { color, pattern }),

  markTourComplete: (id: string) =>
    api.put<{ success: boolean }>(`/users/${id}/tour-complete`),

  setPrimary: (id: string) =>
    api.put<{ success: boolean }>(`/users/${id}/set-primary`),

  getColors: () =>
    api.get<{
      colors: Array<{ hex: string; pattern: string; name: string; available: boolean }>;
    }>('/users/colors'),

  resetPassword: (id: string, mode: 'email' | 'temporary', password?: string) =>
    api.post<{ success: boolean; mode: string }>(`/users/${id}/reset-password`, { mode, password }),

  deleteUser: (id: string) =>
    api.delete<{ success: boolean }>(`/users/${id}`),
};

// Calendar day display type
export type CalendarDayDisplay = 'half-bar' | 'half-box';

// Property API
export const propertyApi = {
  get: () =>
    api.get<{
      property: {
        property_name: string;
        timezone: string;
        default_checkin_time: string;
        default_checkout_time: string;
        property_info: string | null;
        guides_require_booking: boolean;
        calendar_day_display: CalendarDayDisplay;
        welcome_email_bullets?: string | null;
        checkout_email_bullets?: string | null;
        updated_at: string;
      };
    }>('/property'),

  update: (data: {
    property_name?: string;
    timezone?: string;
    default_checkin_time?: string;
    default_checkout_time?: string;
    property_info?: string;
    guides_require_booking?: boolean;
    calendar_day_display?: CalendarDayDisplay;
    welcome_email_bullets?: string;
    checkout_email_bullets?: string;
  }) => api.put<{ success: boolean }>('/property', data),

  emailPreview: (data: {
    type: 'welcome' | 'checkout';
    welcome_email_bullets?: string;
    checkout_email_bullets?: string;
  }) =>
    api.post<{ subject: string; html: string }>('/property/email-preview', data),
};

// Calendar event type for FullCalendar
export interface CalendarEvent {
  id: string;
  title: string;
  start: string;
  end: string;
  allDay: boolean;
  backgroundColor: string;
  borderColor: string;
  textColor: string;
  classNames: string[];
  extendedProps: {
    bookingId: string;
    userId: string | null;
    userName: string | null;
    status: 'pending' | 'approved';
    isOwnBooking: boolean;
    isFriendsBooking: boolean;
    guestName: string | null;
    guestEmail: string | null;
    userMessage: string | null;
    ownerNotes: string | null;
    startTime: string;
    endTime: string;
  };
}

// Search result type
export interface SearchResult {
  date: string;
  status: 'available' | 'approved' | 'pending';
  booking?: {
    id: string;
    user_name: string;
    user_color: string;
    status: string;
    isOwnBooking: boolean;
  };
}

export interface SearchSummary {
  totalDays: number;
  availableDays: number;
  approvedDays: number;
  pendingDays: number;
}

// Calendar API
export const calendarApi = {
  getEvents: (start: string, end: string) =>
    api.get<{
      events: CalendarEvent[];
      userColor: string;
      userRole: 'owner' | 'admin' | 'user';
    }>(`/calendar?start=${start}&end=${end}`),

  checkAvailability: (start: string, end: string) =>
    api.get<{
      available: boolean;
      hasConflict: boolean;
      hasPendingConflict: boolean;
      hasOwnPending: boolean;
      confirmedBookings: Array<{ id: string; start_date: string; end_date: string }>;
      pendingCount: number;
      earliestAvailable: string | null;
      latestAvailable: string | null;
    }>(`/calendar/availability?start=${start}&end=${end}`),

  search: (startDate: string, endDate: string, filter: 'all' | 'available' | 'mine' = 'all') =>
    api.get<{
      results: SearchResult[];
      summary: SearchSummary;
    }>(`/calendar/search?start_date=${startDate}&end_date=${endDate}&filter=${filter}`),
};

// Booking type
export interface Booking {
  id: string;
  user_id: string;
  start_date: string;
  end_date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'approved' | 'declined' | 'cancelled';
  user_message: string | null;
  owner_notes: string | null;
  decision_message: string | null;
  cancellation_reason: string | null;
  guest_name: string | null;
  guest_email: string | null;
  created_at: string;
  decided_at: string | null;
  decided_by: string | null;
  user_name?: string;
  user_email?: string;
  user_color?: string;
  is_friends_booking?: number;
}

// Bookings API
export const bookingsApi = {
  getMine: () => api.get<{ bookings: Booking[] }>('/bookings/mine'),

  getPending: () => api.get<{ bookings: Booking[] }>('/bookings/pending'),

  getAll: (params?: { status?: string; user_id?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.status) searchParams.set('status', params.status);
    if (params?.user_id) searchParams.set('user_id', params.user_id);
    const query = searchParams.toString();
    return api.get<{ bookings: Booking[] }>(`/bookings/all${query ? `?${query}` : ''}`);
  },

  getOne: (id: string) => api.get<{ booking: Booking }>(`/bookings/${id}`),

  create: (data: {
    start_date: string;
    end_date: string;
    start_time?: string;
    end_time?: string;
    message?: string;
    for_user_id?: string;
    guest_name?: string;
    guest_email?: string;
  }) =>
    api.post<{ booking: { id: string }; hasPendingConflicts: boolean; autoApproved?: boolean }>(
      '/bookings',
      data
    ),

  cancel: (id: string, reason: string) => api.put<{ success: boolean }>(`/bookings/${id}/cancel`, { reason }),

  updateNotes: (id: string, notes: string) =>
    api.put<{ success: boolean }>(`/bookings/${id}/notes`, { notes }),

  approve: (id: string, message?: string) =>
    api.put<{ success: boolean }>(`/bookings/${id}/approve`, { message }),

  decline: (id: string, message?: string) =>
    api.put<{ success: boolean }>(`/bookings/${id}/decline`, { message }),

  reassign: (id: string, userId: string, guestName?: string) =>
    api.put<{ success: boolean }>(`/bookings/${id}/reassign`, {
      user_id: userId,
      guest_name: guestName,
    }),

  edit: (id: string, data: {
    start_date?: string;
    end_date?: string;
    start_time?: string;
    end_time?: string;
    guest_name?: string;
    guest_email?: string;
  }) => api.put<{ success: boolean }>(`/bookings/${id}`, data),

  delete: (id: string) => api.delete<{ success: boolean }>(`/bookings/${id}`),
};

// Audit Log types
export interface AuditLogEntry {
  id: string;
  user_id: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  details: string | null;
  created_at: string;
  user_name: string | null;
  user_email: string | null;
}

export interface AuditLogPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

// Audit Log API
export const auditLogApi = {
  getEntries: (params: {
    page?: number;
    limit?: number;
    action?: string;
    user_id?: string;
    start_date?: string;
    end_date?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.page) searchParams.set('page', params.page.toString());
    if (params.limit) searchParams.set('limit', params.limit.toString());
    if (params.action) searchParams.set('action', params.action);
    if (params.user_id) searchParams.set('user_id', params.user_id);
    if (params.start_date) searchParams.set('start_date', params.start_date);
    if (params.end_date) searchParams.set('end_date', params.end_date);

    return api.get<{
      entries: AuditLogEntry[];
      pagination: AuditLogPagination;
    }>(`/audit-log?${searchParams.toString()}`);
  },

  getExportUrl: (params: {
    action?: string;
    user_id?: string;
    start_date?: string;
    end_date?: string;
  }) => {
    const searchParams = new URLSearchParams();
    if (params.action) searchParams.set('action', params.action);
    if (params.user_id) searchParams.set('user_id', params.user_id);
    if (params.start_date) searchParams.set('start_date', params.start_date);
    if (params.end_date) searchParams.set('end_date', params.end_date);

    const base = import.meta.env.PROD ? 'https://api.5h.hellers.com/api' : '/api';
    return `${base}/audit-log/export?${searchParams.toString()}`;
  },
};

// Guide types
export interface Guide {
  id: string;
  title: string;
  slug: string;
  content?: string;
  sortOrder: number;
  isPublished: boolean;
  requiresBooking: boolean;
  createdAt?: string;
  updatedAt: string;
}

// Guides API
export const guidesApi = {
  list: () =>
    api.get<{ guides: Guide[]; hasBookingAccess: boolean }>('/guides'),

  get: (slug: string) => api.get<{ guide: Guide }>(`/guides/${slug}`),

  create: (data: {
    title: string;
    content?: string;
    isPublished?: boolean;
    requiresBooking?: boolean;
  }) => api.post<{ guide: { id: string; slug: string } }>('/guides', data),

  update: (
    id: string,
    data: {
      title?: string;
      slug?: string;
      content?: string;
      isPublished?: boolean;
      requiresBooking?: boolean;
    }
  ) => api.put<{ success: boolean }>(`/guides/${id}`, data),

  reorder: (order: Array<{ id: string; sortOrder: number }>) =>
    api.put<{ success: boolean }>('/guides/reorder', { order }),

  delete: (id: string) => api.delete<{ success: boolean }>(`/guides/${id}`),
};

// Document types
export interface Document {
  id: string;
  displayName: string;
  description: string | null;
  fileName: string;
  fileType: string;
  fileSize: number;
  category: string | null;
  requiresBooking: boolean;
  guidePageId: string | null;
  createdAt: string;
  uploaderName: string | null;
}

// Documents API
export const documentsApi = {
  list: (params?: { category?: string; guidePageId?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.category) searchParams.set('category', params.category);
    if (params?.guidePageId) searchParams.set('guide_page_id', params.guidePageId);
    const qs = searchParams.toString();
    return api.get<{ documents: Document[]; hasBookingAccess: boolean }>(
      `/documents${qs ? `?${qs}` : ''}`
    );
  },

  getCategories: () => api.get<{ categories: string[] }>('/documents/categories'),

  getDownloadUrl: (id: string) => {
    const base = import.meta.env.PROD ? 'https://api.5h.hellers.com/api' : '/api';
    return `${base}/documents/${id}/download`;
  },

  upload: async (data: {
    file: File;
    displayName?: string;
    description?: string;
    category?: string;
    requiresBooking?: boolean;
    guidePageId?: string;
  }) => {
    const formData = new FormData();
    formData.append('file', data.file);
    if (data.displayName) formData.append('displayName', data.displayName);
    if (data.description) formData.append('description', data.description);
    if (data.category) formData.append('category', data.category);
    if (data.requiresBooking) formData.append('requiresBooking', 'true');
    if (data.guidePageId) formData.append('guidePageId', data.guidePageId);

    const API_BASE = import.meta.env.PROD
      ? 'https://api.5h.hellers.com/api'
      : '/api';

    // Include CSRF token for file upload
    const headers: Record<string, string> = {};
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    const response = await fetch(`${API_BASE}/documents`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers,
    });

    const result = await response.json();
    if (!response.ok) {
      throw new ApiRequestError(
        result.error.message,
        result.error.code,
        response.status,
        result.error.field
      );
    }
    return result.data as {
      document: {
        id: string;
        displayName: string;
        fileName: string;
        fileType: string;
        fileSize: number;
      };
      viewUrl?: string;
    };
  },

  uploadImage: async (file: File): Promise<string> => {
    const result = await documentsApi.upload({ file });
    return result.viewUrl || '';
  },

  update: (
    id: string,
    data: {
      displayName?: string;
      description?: string;
      category?: string;
      requiresBooking?: boolean;
      guidePageId?: string | null;
    }
  ) => api.put<{ success: boolean }>(`/documents/${id}`, data),

  delete: (id: string) => api.delete<{ success: boolean }>(`/documents/${id}`),
};

// Export API
export const exportApi = {
  // Download a single booking as ICS file
  downloadBookingIcs: async (id: string): Promise<void> => {
    const base = import.meta.env.PROD ? 'https://api.5h.hellers.com/api' : '/api';
    const response = await fetch(`${base}/export/booking/${id}.ics`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Export failed' } }));
      throw new Error(error.error?.message || 'Export failed');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `booking-${id}.ics`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  // Download all user's bookings as ICS file
  downloadMyBookingsIcs: async (): Promise<void> => {
    const base = import.meta.env.PROD ? 'https://api.5h.hellers.com/api' : '/api';
    const response = await fetch(`${base}/export/my-bookings.ics`, {
      credentials: 'include',
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Export failed' } }));
      throw new Error(error.error?.message || 'Export failed');
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'my-bookings.ics';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  getSubscriptionToken: () =>
    api.get<{
      token: string | null;
      subscriptionUrl: string | null;
      webcalUrl: string | null;
      expiresAt: string | null;
    }>('/export/subscription-token'),

  createSubscriptionToken: () =>
    api.post<{
      token: string;
      expiresAt: string;
      subscriptionUrl: string;
      webcalUrl: string;
    }>('/export/subscription-token'),

  revokeSubscriptionToken: () =>
    api.delete<{ success: boolean }>('/export/subscription-token'),
};

// Import types
export interface PreviewEvent {
  uid: string;
  summary: string;
  description?: string;
  startDate: string;
  endDate: string;
  hasConflict: boolean;
  conflictBookingId?: string;
  assignedUserId?: string;
}

export interface ImportUser {
  id: string;
  name: string;
  email: string;
  color: string;
}

// Import API
export const importApi = {
  preview: async (file: File) => {
    const formData = new FormData();
    formData.append('file', file);

    const API_BASE = import.meta.env.PROD
      ? 'https://api.5h.hellers.com/api'
      : '/api';

    // Include CSRF token for file upload
    const headers: Record<string, string> = {};
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    const response = await fetch(`${API_BASE}/import/ical/preview`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers,
    });

    const result = await response.json();
    if (!response.ok) {
      throw new ApiRequestError(
        result.error.message,
        result.error.code,
        response.status,
        result.error.field
      );
    }
    return result.data as {
      events: PreviewEvent[];
      users: ImportUser[];
    };
  },

  confirm: (
    events: Array<{
      uid: string;
      summary: string;
      description?: string;
      startDate: string;
      endDate: string;
      assignedUserId: string;
    }>
  ) =>
    api.post<{
      created: Array<{ id: string; summary: string }>;
      skipped: Array<{ summary: string; reason: string }>;
    }>('/import/ical/confirm', { events }),
};

// Stats API
export interface UserStats {
  userId: string;
  userName: string;
  userColor: string;
  userColorPattern: string;
  totalNights: number;
  totalStays: number;
  shortestStay: number;
  longestStay: number;
  averageStay: number;
  percentageOfTotal: number;
}

export interface MonthStats {
  month: string;
  monthNumber: number;
  nights: number;
  stays: number;
}

export interface OverallStats {
  totalNights: number;
  totalStays: number;
  occupancyRate: number;
  averageStayLength: number;
  busiestMonth: { month: string; nights: number } | null;
  busiestWeek: { weekOf: string; nights: number } | null;
}

export interface StatsResponse {
  year: number;
  availableYears: number[];
  overall: OverallStats;
  byUser: UserStats[];
  byMonth: MonthStats[];
}

export const statsApi = {
  getStats: (year?: number) =>
    api.get<StatsResponse>(`/stats${year ? `?year=${year}` : ''}`),
};

// Rewards types
export interface TierDefinition {
  level: number;
  name: string;
  description: string;
  nightsRequired: number;
  badgeKey: string;
  badgeUrl: string;
}

export interface TierDefinitionUpdate {
  level: number;
  name: string;
  description: string;
  nightsRequired: number;
  badgeKey: string;
}

export interface EarnedBadge {
  id: string;
  tierLevel: number;
  tierName: string;
  badgeKey: string;
  badgeUrl: string;
  year: number;
  earnedAt: string;
}

export interface CurrentYearTier {
  level: number;
  name: string;
  badgeKey: string;
  badgeUrl: string;
}

export interface RewardStatus {
  userId: string;
  totalNights: number;
  currentTier: {
    level: number;
    name: string;
    description: string;
    badgeKey: string;
    badgeUrl: string;
    achievedAt: string | null;
  };
  nextTier: {
    level: number;
    name: string;
    nightsRequired: number;
    nightsRemaining: number;
    progressPercentage: number;
  } | null;
  currentYearNights?: number;
  currentYearTier?: CurrentYearTier;
  earnedBadges?: EarnedBadge[];
}

// Rewards API
export const rewardsApi = {
  getStatus: () => api.get<RewardStatus>('/rewards/status'),

  getTiers: (year?: number | null) =>
    api.get<{ tiers: TierDefinition[]; year?: number | null; source?: 'lifetime' | 'annual' }>(
      `/rewards/tiers${year ? `?year=${year}` : ''}`
    ),

  updateTiers: (tiers: TierDefinitionUpdate[]) =>
    api.put<{ success: boolean }>('/rewards/tiers', { tiers }),

  updateTiersForYear: (year: number, tiers: TierDefinitionUpdate[]) =>
    api.put<{ success: boolean }>(`/rewards/tiers?year=${year}`, { tiers }),

  getYears: () => api.get<{ years: number[] }>('/rewards/years'),
};

// Badges API (uploaded images served from the API, with fallback to built-in static badges)
export const badgesApi = {
  getUrl: (badgeKey: string, gender: 'male' | 'female') =>
    `${API_BASE_URL}/badges/${badgeKey}-${gender}.png`,

  upload: async (data: { badgeKey: string; gender: 'male' | 'female'; file: File }) => {
    const formData = new FormData();
    formData.append('file', data.file);
    formData.append('badgeKey', data.badgeKey);
    formData.append('gender', data.gender);

    // Include CSRF token for file upload
    const headers: Record<string, string> = {};
    const csrfToken = getCsrfToken();
    if (csrfToken) {
      headers['X-CSRF-Token'] = csrfToken;
    }

    const response = await fetch(`${API_BASE_URL}/badges/upload`, {
      method: 'POST',
      body: formData,
      credentials: 'include',
      headers,
    });

    const result = await response.json();
    if (!response.ok) {
      throw new ApiRequestError(
        result.error.message,
        result.error.code,
        response.status,
        result.error.field
      );
    }

    return result.data as {
      success: boolean;
      badgeKey: string;
      gender: 'male' | 'female';
      fileName: string;
      url: string;
    };
  },
};

// Announcements API
export interface AnnouncementSendResponse {
  success: boolean;
  totalRecipients: number;
  successCount: number;
  failureCount: number;
  failures?: Array<{ email: string; error: string }>;
}

export interface AnnouncementRecipient {
  id: string;
  name: string;
  is_active: number;
}

export const announcementsApi = {
  getRecipients: (includeInactive: boolean = false) =>
    api.get<{ recipients: AnnouncementRecipient[] }>(
      `/announcements/recipients?includeInactive=${includeInactive}`
    ),

  preview: (includeInactive: boolean = false) =>
    api.get<{ recipientCount: number }>(
      `/announcements/preview?includeInactive=${includeInactive}`
    ),

  send: (data: {
    subject: string;
    message: string;
    userIds?: string[];
    testMode?: boolean;
  }) => api.post<AnnouncementSendResponse>('/announcements/send', data),
};
